// @ts-nocheck
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar, Mail, Shield, TrendingUp, Package } from "lucide-react";
import { UserRole } from "@/state/session";

interface UserViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    created_at: string;
  } | null;
}

const roleLabels: Record<UserRole, string> = {
  admin: "Administrador",
  gestor: "Gestor",
  funcionario: "Funcionário",
};

const roleBadgeVariants: Record<UserRole, "default" | "secondary" | "destructive"> = {
  admin: "destructive",
  gestor: "default",
  funcionario: "secondary",
};

interface UserStats {
  totalVendas: number;
  totalEntradas: number;
  ultimoLogin: string | null;
}

export function UserViewDialog({ open, onOpenChange, user }: UserViewDialogProps) {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      loadUserStats();
    }
  }, [open, user]);

  const loadUserStats = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Count vendas created by user
      const { count: vendasCount } = await supabase
        .from('venda')
        .select('*', { count: 'exact', head: true })
        .eq('created_by' as any, user.id) as any;

      // Count entradas created by user
      const { count: entradasCount } = await supabase
        .from('estoque_entrada')
        .select('*', { count: 'exact', head: true })
        .eq('created_by' as any, user.id) as any;

      // Get last login from session_audit
      const { data: lastSession } = await supabase
        .from('session_audit')
        .select('login_at')
        .eq('user_id' as any, user.id)
        .order('login_at', { ascending: false })
        .limit(1)
        .single() as any;

      setStats({
        totalVendas: vendasCount || 0,
        totalEntradas: entradasCount || 0,
        ultimoLogin: lastSession?.login_at || null,
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setStats({
        totalVendas: 0,
        totalEntradas: 0,
        ultimoLogin: null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Detalhes do Usuário</DialogTitle>
          <DialogDescription>
            Informações completas sobre {user.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Nome</p>
                <p className="font-medium">{user.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">E-mail</p>
                <p className="font-medium break-all">{user.email}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Função</p>
                <Badge variant={roleBadgeVariants[user.role]} className="mt-1">
                  {roleLabels[user.role]}
                </Badge>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Data de Criação</p>
                <p className="font-medium">
                  {new Date(user.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Statistics */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm">Estatísticas</h4>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Total de Vendas</p>
                    <p className="font-semibold text-lg">{stats.totalVendas}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Total de Entradas</p>
                    <p className="font-semibold text-lg">{stats.totalEntradas}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Último Login</p>
                    <p className="font-medium">
                      {stats.ultimoLogin
                        ? new Date(stats.ultimoLogin).toLocaleString('pt-BR')
                        : 'Nunca'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Não foi possível carregar as estatísticas</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
